import { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';
import { formatVolume } from '../utils/priceChartStats';

const EMA_COLOR = '#2563eb';
const SMA50_COLOR = '#d97706';
const SMA200_COLOR = '#7c3aed';
const VOLUME_AVG_COLOR = '#64748b';
const UP = '#16a34a';
const DOWN = '#dc2626';

function themeColors() {
    const styles = getComputedStyle(document.body);
    const dark = document.body.getAttribute('data-theme') === 'dark';
    const pick = (name, fallback) => styles.getPropertyValue(name).trim() || fallback;
    return {
        background: pick('--bg-1', dark ? '#0B1220' : '#ffffff'),
        text: pick('--text-2', dark ? '#cbd5e1' : '#64748b'),
        grid: dark ? 'rgba(148, 163, 184, 0.16)' : '#e5e7eb',
        border: pick('--border', dark ? '#334155' : '#e5e7eb'),
    };
}

function linePoints(rows, key) {
    return rows.map((row) => (
        row[key] == null ? { time: row.date } : { time: row.date, value: row[key] }
    ));
}

function bindRange(charts) {
    let lock = false;
    const unsubscribers = charts.map((chart, index) => chart.timeScale().subscribeVisibleLogicalRangeChange((range) => {
        if (lock || !range) return;
        lock = true;
        charts.forEach((other, otherIndex) => {
            if (otherIndex !== index) other.timeScale().setVisibleLogicalRange(range);
        });
        lock = false;
    }));
    return () => unsubscribers.forEach((unsubscribe, index) => {
        if (typeof unsubscribe === 'function') charts[index].timeScale().unsubscribeVisibleLogicalRangeChange(unsubscribe);
    });
}

function bindCrosshair(entries) {
    entries.forEach(({ chart, series }) => {
        chart.subscribeCrosshairMove((param) => {
            entries.forEach((other) => {
                if (other.chart === chart) return;
                if (!param?.time) {
                    other.chart.clearCrosshairPosition();
                    return;
                }
                const point = param.seriesData?.get?.(series);
                const price = point?.close ?? point?.value ?? other.rows.find((row) => row.date === param.time)?.close;
                if (price == null) {
                    other.chart.clearCrosshairPosition();
                    return;
                }
                other.chart.setCrosshairPosition(price, param.time, other.series);
            });
        });
    });
}

/**
 * Each chart pane is mounted inside a wrapper div whose CSS sets an
 * explicit height (via .technical-chart__price / .technical-chart__pane).
 * We use autoSize so the library's own ResizeObserver keeps the canvas
 * in sync with the CSS-driven container size.
 */
function makeChart(container, colors, opts = {}) {
    return createChart(container, {
        autoSize: false,              // Disabling autoSize to use our custom ResizeObserver
        layout: {
            background: { type: 'solid', color: colors.background },
            textColor: colors.text,
            attributionLogo: false,
        },
        grid: { vertLines: { visible: false }, horzLines: { color: colors.grid } },
        crosshair: { mode: CrosshairMode.Normal },
        rightPriceScale: {
            borderColor: colors.border,
            scaleMargins: { top: 0.08, bottom: 0.08 },
        },
        timeScale: {
            visible: opts.showTime ?? false,
            borderColor: colors.border,
            rightOffset: 4,
            minBarSpacing: 3,
        },
        handleScroll: { vertTouchDrag: false },
        kineticScroll: { mouse: true, touch: true },
        localization: opts.priceFormatter ? { priceFormatter: opts.priceFormatter } : undefined,
    });
}

export default function TechnicalChart({ rows, interval = 'daily' }) {
    const priceRef = useRef(null);
    const volumeRef = useRef(null);
    const rsiRef = useRef(null);
    const macdRef = useRef(null);

    const [overlays, setOverlays] = useState({ ma: true, bb: false, st: false });

    const toggleOverlay = (key) => setOverlays(prev => ({ ...prev, [key]: !prev[key] }));

    useEffect(() => {
        const nodes = [priceRef.current, volumeRef.current, rsiRef.current, macdRef.current];
        if (nodes.some((n) => !n) || !rows.length) return undefined;

        const colors = themeColors();

        const priceChart = makeChart(priceRef.current, colors);
        const volumeChart = makeChart(volumeRef.current, colors, {
            priceFormatter: (p) => formatVolume(p),
        });
        const rsiChart = makeChart(rsiRef.current, colors);
        const macdChart = makeChart(macdRef.current, colors, { showTime: true });
        const charts = [priceChart, volumeChart, rsiChart, macdChart];

        // ── Candlestick ──
        const candles = priceChart.addCandlestickSeries({
            upColor: UP, downColor: DOWN,
            borderUpColor: UP, borderDownColor: DOWN,
            wickUpColor: UP, wickDownColor: DOWN,
        });
        candles.setData(rows.map((row) => ({
            time: row.date, open: row.open, high: row.high, low: row.low, close: row.close,
        })));

        // ── Moving Averages ──
        if (overlays.ma) {
            [['ema20', EMA_COLOR], ['sma50', SMA50_COLOR], ['sma200', SMA200_COLOR]].forEach(([key, color]) => {
                const s = priceChart.addLineSeries({
                    color, lineWidth: 2, priceLineVisible: false,
                    lastValueVisible: false, crosshairMarkerVisible: false,
                });
                s.setData(linePoints(rows, key));
            });
        }

        // ── Bollinger Bands ──
        if (overlays.bb) {
            ['bbUpper', 'bbMiddle', 'bbLower'].forEach(key => {
                const s = priceChart.addLineSeries({
                    color: key === 'bbMiddle' ? '#a855f7' : 'rgba(168, 85, 247, 0.4)',
                    lineWidth: key === 'bbMiddle' ? 1 : 2,
                    lineStyle: key === 'bbMiddle' ? 2 : 0,
                    priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
                });
                s.setData(linePoints(rows, key));
            });
        }

        // ── Supertrend ──
        if (overlays.st) {
            const upS = priceChart.addLineSeries({
                color: UP, lineWidth: 2, priceLineVisible: false,
                lastValueVisible: false, crosshairMarkerVisible: false,
            });
            const downS = priceChart.addLineSeries({
                color: DOWN, lineWidth: 2, priceLineVisible: false,
                lastValueVisible: false, crosshairMarkerVisible: false,
            });
            const upData = [], downData = [];
            rows.forEach(row => {
                if (row.stValue != null) {
                    (row.stTrend === 1 ? upData : downData).push({ time: row.date, value: row.stValue });
                }
            });
            upS.setData(upData);
            downS.setData(downData);
        }

        // ── Volume ──
        const volume = volumeChart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
        volume.setData(rows.map((row) => (
            row.volume == null
                ? { time: row.date }
                : { time: row.date, value: row.volume, color: row.close >= row.open ? UP : DOWN }
        )));
        const volumeAvg = volumeChart.addLineSeries({
            color: VOLUME_AVG_COLOR, lineWidth: 2,
            priceLineVisible: false, lastValueVisible: false, crosshairMarkerVisible: false,
        });
        volumeAvg.setData(linePoints(rows, 'volumeAvg20'));

        // ── RSI ──
        const rsi = rsiChart.addLineSeries({ color: EMA_COLOR, lineWidth: 2, priceLineVisible: false });
        rsi.setData(linePoints(rows, 'rsi14'));
        [30, 70].forEach((price) => {
            rsi.createPriceLine({
                price, color: colors.border, lineWidth: 1, lineStyle: 2, axisLabelVisible: true, title: '',
            });
        });

        // ── MACD ──
        const macdHist = macdChart.addHistogramSeries({ priceLineVisible: false, lastValueVisible: false });
        macdHist.setData(rows.map((row) => (
            row.macdHist == null
                ? { time: row.date }
                : { time: row.date, value: row.macdHist, color: row.macdHist >= 0 ? UP : DOWN }
        )));
        const macdLine = macdChart.addLineSeries({ color: EMA_COLOR, lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
        const macdSignal = macdChart.addLineSeries({ color: SMA50_COLOR, lineWidth: 2, priceLineVisible: false, lastValueVisible: false });
        macdLine.setData(linePoints(rows, 'macdLine'));
        macdSignal.setData(linePoints(rows, 'macdSignal'));

        // ── Manual ResizeObserver to sync layout and fitContent ──
        const doResize = (w) => {
            if (!w || w < 10) return;
            const floorW = Math.floor(w);
            priceChart.resize(floorW, 512);
            volumeChart.resize(floorW, 112);
            rsiChart.resize(floorW, 112);
            macdChart.resize(floorW, 128);
            priceChart.timeScale().fitContent();
        };

        const resizeObserver = new ResizeObserver((entries) => {
            for (let entry of entries) {
                if (entry.target === priceRef.current) {
                    doResize(entry.contentRect.width);
                }
            }
        });
        resizeObserver.observe(priceRef.current);

        // Initial paint resize
        let rafId = requestAnimationFrame(() => {
            const initialW = priceRef.current?.getBoundingClientRect().width;
            if (initialW) doResize(initialW);
        });

        // ── Sync range + crosshair ──
        const unbindRange = bindRange(charts);
        bindCrosshair([
            { chart: priceChart, series: candles, rows },
            { chart: volumeChart, series: volume, rows },
            { chart: rsiChart, series: rsi, rows },
            { chart: macdChart, series: macdHist, rows },
        ]);

        return () => {
            resizeObserver.disconnect();
            if (rafId) cancelAnimationFrame(rafId);
            unbindRange();
            charts.forEach((chart) => chart.remove());
        };
    }, [rows, overlays]);

    return (
        <div className="technical-chart">
            <div className="technical-chart__legend" aria-hidden="true" style={{ justifyContent: 'space-between' }}>
                <div style={{ display: 'flex', gap: '0.85rem', flexWrap: 'wrap' }}>
                    {overlays.ma && (
                        <>
                            <span className="technical-chart__swatch technical-chart__swatch--ema">20-{interval === 'weekly' ? 'week' : 'day'} EMA</span>
                            <span className="technical-chart__swatch technical-chart__swatch--sma50">50-{interval === 'weekly' ? 'week' : 'day'} SMA</span>
                            <span className="technical-chart__swatch technical-chart__swatch--sma200">200-{interval === 'weekly' ? 'week' : 'day'} SMA</span>
                        </>
                    )}
                    {overlays.bb && (
                        <span className="technical-chart__swatch" style={{ color: '#a855f7' }}>Bollinger (20,2)</span>
                    )}
                    {overlays.st && (
                        <span className="technical-chart__swatch" style={{ color: UP }}>Supertrend (10,3)</span>
                    )}
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                        className={`price-period-btn ${overlays.ma ? 'price-period-btn--active' : ''}`}
                        onClick={() => toggleOverlay('ma')}
                        style={{ padding: '0.15rem 0.5rem', fontSize: '0.7rem' }}
                    >MAs</button>
                    <button
                        className={`price-period-btn ${overlays.bb ? 'price-period-btn--active' : ''}`}
                        onClick={() => toggleOverlay('bb')}
                        style={{ padding: '0.15rem 0.5rem', fontSize: '0.7rem' }}
                    >Bollinger Bands</button>
                    <button
                        className={`price-period-btn ${overlays.st ? 'price-period-btn--active' : ''}`}
                        onClick={() => toggleOverlay('st')}
                        style={{ padding: '0.15rem 0.5rem', fontSize: '0.7rem' }}
                    >Supertrend</button>
                </div>
            </div>
            <div ref={priceRef} className="technical-chart__price" data-testid="technical-price-pane" />
            <div className="technical-chart__sub">
                <div className="technical-chart__pane-label">Volume</div>
                <div ref={volumeRef} className="technical-chart__pane" data-testid="technical-volume-pane" />
            </div>
            <div className="technical-chart__sub">
                <div className="technical-chart__pane-label">RSI 14</div>
                <div ref={rsiRef} className="technical-chart__pane" data-testid="technical-rsi-pane" />
            </div>
            <div className="technical-chart__sub">
                <div className="technical-chart__pane-label">MACD</div>
                <div ref={macdRef} className="technical-chart__pane technical-chart__pane--macd" data-testid="technical-macd-pane" />
            </div>
        </div>
    );
}
